/**
 * /tv — shop floor display. Unauthenticated, non-interactive.
 *
 * PRIVACY: first names only — NOT first name plus last initial. The walk-in
 * document carries a full name and a phone number and renders neither. This
 * screen faces a public waiting room with nothing authenticating in front of
 * it, so the rule stays as tight as it already is: an initial would put more
 * of a stranger's identity on a wall than the room needs, and the queue
 * record does not even carry one.
 *
 * Phase 1a orders the queue by position, not ETA — see StayOrGo.
 * Phase 1b adds "(~18 min)" parentheticals once GHL lands.
 */

import { useEffect, useState } from 'react';
import { useShopConfig } from '../../hooks/useShopConfig';
import { useClockedIn, useWalkInQueue } from '../../hooks/useShopState';
import { useKioskGround, KIOSK_NAVY } from '../../components/kiosk/useKioskGround';

/** "started 12 min ago" — recomputed on a 30s tick. */
function useTick(ms = 30_000) {
  const [, setN] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setN((n) => n + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
}



export default function TvDisplay() {
  useTick();
  // Navy on html and body too: the shell alone leaves the page colour showing
  // under the content, which on a wall-mounted screen is a bright band across
  // the bottom of the room.
  useKioskGround(KIOSK_NAVY);
  const { barbers, shop } = useShopConfig();
  const { inOrder: allOnClock } = useClockedIn();
  // Same rule as the kiosk picker: the board advertises who is taking
  // walk-ins, not who happens to be clocked in.
  const inOrder = allOnClock.filter(
    ([slug]) => barbers.find((b) => b.slug === slug)?.takesWalkIns !== false
  );
  const { cutting, waiting } = useWalkInQueue();

  const name = (slug: string | null) => barbers.find((b) => b.slug === slug)?.display_name ?? '';
  const minsAgo = (ms: number) => Math.max(0, Math.round((Date.now() - ms) / 60_000));

  // Assigned-and-waiting vs. queued behind everyone.
  const assignedWaiting = waiting.filter((w) => w.assignedTo);
  const queued = waiting.filter((w) => !w.assignedTo);


  const portrait = (slug: string | null) =>
    barbers.find((b) => b.slug === slug)?.portrait.kiosk ?? '';

  // Sized to read from ~15 ft on a 1080p screen: body copy sits where a
  // laptop layout would put a headline.
  const sectionLabel = 'font-oswald text-[30px] font-medium uppercase tracking-[0.22em] text-gold';
  const ring = 'rounded-full border-2 border-gold object-cover';
  const emptyLine = 'font-newsreader text-[44px] italic text-paper/55';

  return (
    <div className="kd-kiosk-root flex flex-col bg-navy px-16 py-12 text-paper">
      <header className="flex shrink-0 items-center gap-6 border-b border-paper/15 pb-8">
        <img
          src="/assets/logo/kd-crest.png"
          alt=""
          role="presentation"
          className="h-20 w-20 object-contain"
        />
        <span className="font-oswald text-[26px] font-medium uppercase tracking-[0.26em] text-gold">
          {shop.name}
        </span>
      </header>

      <div className="grid flex-1 grid-cols-2 gap-20 pt-12">
        <section>
          <h2 className={sectionLabel}>In the chair</h2>
          <ul className="mt-10 space-y-8">
            {cutting.map((w) => (
              <li key={w.id} className="flex items-center gap-6 border-b border-paper/15 pb-7">
                <img src={portrait(w.assignedTo)} alt="" className={`h-24 w-24 shrink-0 ${ring}`} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-newsreader text-[56px] font-medium leading-tight text-paper">
                    {name(w.assignedTo)}
                  </span>
                  <span className="block font-oswald text-[26px] uppercase tracking-[0.16em] text-paper/70">
                    {w.firstName} · {minsAgo(w.checkedInAt.toMillis())} min
                  </span>
                </span>
              </li>
            ))}
            {cutting.length === 0 && <li className={emptyLine}>All chairs open</li>}
          </ul>
        </section>

        <section>
          <h2 className={sectionLabel}>Up next</h2>
          <ul className="mt-10 space-y-6">
            {assignedWaiting.map((w) => (
              <li key={w.id} className="font-newsreader text-[48px] leading-tight text-paper">
                {w.firstName}
                <span className="px-4 text-gold">→</span>
                {name(w.assignedTo)}
              </li>
            ))}
          </ul>

          {queued.length > 0 && (
            <>
              <h3 className="mt-14 font-oswald text-[24px] font-medium uppercase tracking-[0.22em] text-paper/55">
                In line
              </h3>
              <ol className="mt-6 space-y-4">
                {queued.map((w, i) => (
                  <li
                    key={w.id}
                    className="flex items-baseline gap-5 font-newsreader text-[40px] text-paper/85"
                  >
                    <span className="w-12 shrink-0 text-gold">{i + 1}</span>
                    <span>
                      {w.firstName}
                      {w.preferredBarber && (
                        <span className="text-paper/60">
                          {' '}
                          — waiting for {name(w.preferredBarber)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </>
          )}

          {waiting.length === 0 && <p className={emptyLine}>Nobody waiting</p>}
        </section>
      </div>

      <footer className="flex shrink-0 flex-wrap items-center gap-6 border-t border-paper/15 pt-8">
        {inOrder.map(([slug, b]) => (
          <span
            key={slug}
            className="flex items-center gap-4 rounded-pill border border-paper/20 py-2 pl-2 pr-7"
          >
            <img src={portrait(slug)} alt="" className={`h-16 w-16 ${ring}`} />
            <span className="grid h-10 w-10 place-items-center rounded-full bg-gold font-oswald text-[24px] text-navy">
              {b.position}
            </span>
            <span className="font-newsreader text-[36px] text-paper">{name(slug)}</span>
          </span>
        ))}
        {inOrder.length === 0 && (
          <span className="font-newsreader text-[36px] italic text-paper/55">Nobody on the clock</span>
        )}
      </footer>
    </div>
  );
}
